import { Col, DatePicker, Input, Row } from 'antd'
import {
  DesignSubmittal,
  useDesignSubmittalLogContext,
} from '../../../../../../DesignSubmittalLogContext'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import PayItemSelect from 'components/CIMSSelect/common/PayItemSelect'
import ExternalLinkEditor from 'components/ExternalLinkEditor'
import { DSStatus, SharePointRegexValidation } from 'shared'
import { HelpMessage } from 'utils/constants'
import { useState } from 'react'
import { DisabledReason, useDisabledOptions } from 'hooks/useDisabledOptions'
import CustomFieldEditor from 'components/CustomFields/CustomFieldEditor'
const help = HelpMessage.DesignSubmittals.Log

export const EditDesignSubmittalModalBody: React.FC<DesignSubmittal> = ({
  statusId,
  packageNumber,
}) => {
  const {
    quickLinks,
    metaFetching,
    allPackageNumbers,
    permissions: { canNumberName },
    selectOptions: {
      actionsOptions,
      disciplinesOptions,
      typesOptions,
      orgsOptions,
      fdcOptions,
    },
  } = useDesignSubmittalLogContext()
  const statusDisciplineAction = statusId === DSStatus.DisciplineAction.id
  const { invalidChars, whitespace } = SharePointRegexValidation
  const [filteredPackageNumbers] = useState<string[]>(
    allPackageNumbers.filter((number) => number !== packageNumber)
  )
  const [numberNameDisabledOptions, { some: numberNameDisabled }] =
    useDisabledOptions({
      disabled: !canNumberName,
      reason: 'missing permission',
    })
  const [statusDisabledOptions, { some: statusDisabled }] = useDisabledOptions({
    disabled: !statusDisciplineAction,
    reason: {
      why: 'only status',
      statusName: DSStatus.DisciplineAction.name,
    },
  })
  const lockedReason: DisabledReason = {
    why: 'invalid status',
    statusName: DSStatus.ReturnedToDesignBuilder.name,
  }
  const [lockedDisabledOptions, { some: lockedDisabled }] = useDisabledOptions({
    disabled: statusId === DSStatus.ReturnedToDesignBuilder.id,
    reason: lockedReason,
  })
  return (
    <>
      <Row gutter={30}>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="packageNumber"
            label="Package Number"
            requiredMessage="Please enter a package number"
            invalid={[
              {
                testPattern: invalidChars.regex,
                message: invalidChars.message,
              },
              {
                testPattern: whitespace.regex,
                message: whitespace.message,
              },
            ]}
            customValidator={{
              validator: (packageNumber) =>
                !filteredPackageNumbers.includes(packageNumber),
              message: 'Package Number already in use',
            }}
            validateFirst="parallel"
            disabled={numberNameDisabledOptions}
            help={help.PackageNumber}
          >
            <Input disabled={numberNameDisabled} allowClear />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="packageName"
            label="Package Name"
            requiredMessage="Please enter a package name"
            invalid={[
              {
                testPattern: invalidChars.regex,
                message: invalidChars.message,
              },
              {
                testPattern: whitespace.regex,
                message: whitespace.message,
              },
            ]}
            validateFirst="parallel"
            disabled={numberNameDisabledOptions}
            help={help.PackageName}
          >
            <Input disabled={numberNameDisabled} allowClear />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row gutter={30}>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="type"
            label="Type"
            requiredMessage="Please select a type"
            help={help.Type}
          >
            <CIMSSelect options={typesOptions} />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="organization"
            label="Submitting Organization"
            requiredMessage="Please select and organization"
            help={help.SubmittingOrg}
          >
            <CIMSSelect options={orgsOptions} />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="requestedActionDate"
            label="Requested Action Date"
            requiredMessage="Please enter a requested action date"
            help={help.RequestedActionDate}
          >
            <DatePicker allowClear />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="contractDueDate"
            label="Contract Due Date"
            help={help.ContractDueDate}
          >
            <DatePicker allowClear />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row gutter={30}>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="actions"
            label="Action(s)"
            help={help.Actions}
            requiredMessage={
              statusDisciplineAction ? 'Please select at least one action' : ''
            }
            disabled={statusDisabledOptions}
          >
            <CIMSSelect
              multiple
              disabled={statusDisabled}
              options={actionsOptions}
            />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="disciplines"
            label="Discipline(s)"
            help={help.Disciplines}
            requiredMessage={
              statusDisciplineAction
                ? 'Please select at least one discipline'
                : ''
            }
            disabled={statusDisabledOptions}
          >
            <CIMSSelect
              multiple
              disabled={statusDisabled}
              options={disciplinesOptions}
            />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}></Col>
      </Row>
      <Row>
        <Col xs={24}>
          <CIMSFormItem
            name="payItems"
            label="Pay Item(s)"
            help={help.PayItems}
          >
            <PayItemSelect multiple />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row gutter={30}>
        <Col xs={24}>
          <CIMSFormItem
            name="fdcs"
            label="Field Design Change(s)"
            help={help.FDCs}
          >
            <CIMSSelect multiple options={fdcOptions} />
          </CIMSFormItem>
        </Col>
      </Row>
      <CustomFieldEditor
        alreadyDisabled={lockedDisabled ? lockedReason : false}
        hideBooleans
      />
      <CIMSFormItem name="externalLinks" disabled={lockedDisabledOptions}>
        <ExternalLinkEditor
          disabled={lockedDisabled}
          quickLinkOptions={{ quickLinks, loading: metaFetching }}
          messages={{ editPackageNumberName: true }}
        />
      </CIMSFormItem>
    </>
  )
}
